import { Expression, systemVariableExpression, triggerParameterExpression, functionExpression, Argument, overloadedFunctionExpression, ExpressionCategory } from 'app/model/expressions/expression';
import { GlobalVariables } from 'app/services/globals';

/**
 * Stores and manages registered system expressions.
 */
export class ExpressionRegistry {
    private static _expressions = new Set<Expression>();
    private static _systemVariableExpressions = new Set<Expression>();
    private static _functionExpressions = new Set<Expression>();
    private static _triggerParameters = new Set<Expression>();

    private static _isInitialized = false;

    public static get systemExpressions(): Set<Expression> {
        if (!ExpressionRegistry._isInitialized) {
            ExpressionRegistry.initialize();
            ExpressionRegistry._isInitialized = true;
        }

        return this._systemVariableExpressions;
    }

    public static get functionExpressions(): Set<Expression> {
        if (!ExpressionRegistry._isInitialized) {
            ExpressionRegistry.initialize();
            ExpressionRegistry._isInitialized = true;
        }

        return this._functionExpressions;
    }

    public static get triggerParameters(): Set<Expression> {
        if (!ExpressionRegistry._isInitialized) {
            ExpressionRegistry.initialize();
            ExpressionRegistry._isInitialized = true;
        }

        return this._triggerParameters;
    }

    /**
     * Returns the registered system expressions.
     */
    public static get expressions(): Set<Expression> {
        if (!ExpressionRegistry._isInitialized) {
            ExpressionRegistry.initialize();
            ExpressionRegistry._isInitialized = true;
        }

        return this._expressions;
    }

    /**
     * Registers one or more expressions.
     * @param expressions The expressions to register.
     */
    public static register(...expressions: Expression[]): void {
        expressions.forEach((expression) => {
            ExpressionRegistry._expressions.add(expression);
            if (expression.category === ExpressionCategory.SystemVariable) {
                this._systemVariableExpressions.add(expression);
            } else if (expression.category === ExpressionCategory.Function) {
                this._functionExpressions.add(expression);
            } else if (expression.category === ExpressionCategory.TriggerParameter) {
                this._triggerParameters.add(expression);
            }
        });
    }

    private static initialize(): void {
        const sysVarExpressions = [
            systemVariableExpression(__IS_FABRIC_DPE__ ? 'Workspace ID' : __IS_A365__ ? 'Workspace name' : 'Data factory name',
                'pipeline().DataFactory',
                `${__IS_FABRIC_DPE__ ? 'ID' : 'Name'} of the ${__IS_A365__ || __IS_FABRIC_DPE__ ? 'workspace' : 'data factory'} the pipeline run is running within`).returns('String'),
            systemVariableExpression(`Pipeline ${__IS_FABRIC_DPE__ ? 'ID' : 'Name'}`, `pipeline().Pipeline`, `${__IS_FABRIC_DPE__ ? 'ID' : 'Name'} of the pipeline`).returns('String'),
            systemVariableExpression('Pipeline run ID', 'pipeline().RunId', 'ID of the specific pipeline run').returns('String'),
            systemVariableExpression('Pipeline group ID', 'pipeline().GroupId', 'ID of the group to which the pipeline run belongs').returns('String'),
            systemVariableExpression('Pipeline trigger type', 'pipeline().TriggerType', 'Type of the trigger that invoked the pipeline (Manual, Scheduler)').returns('String'),
            systemVariableExpression('Pipeline trigger ID', 'pipeline().TriggerId', 'ID of the trigger that invokes the pipeline').returns('String'),
            ...(!__IS_FABRIC_DPE__ ? [systemVariableExpression('Pipeline trigger name', 'pipeline().TriggerName', 'Name of the trigger that invokes the pipeline').returns('String')] : []),
            systemVariableExpression('Pipeline trigger time', 'pipeline().TriggerTime', 'Time when the trigger that invoked the pipeline. The trigger time is the actual' +
                ' fired time, not the scheduled time. For example, 13:20:08.0149599Z is returned instead of 13:20:00.00Z').returns('String'),
            systemVariableExpression('Pipeline triggered by pipeline name', 'pipeline()?.TriggeredByPipelineName', 'Name of the pipeline that triggered this pipeline. ' +
                'Applicable when a pipeline run is triggered by an Execute Pipeline activity; Evaluates to Null when used in other circumstances.').returns('String'),
            systemVariableExpression('Pipeline triggered by pipeline run ID', 'pipeline()?.TriggeredByPipelineRunId', 'Run ID of the pipeline that triggered this pipeline. ' +
                'Applicable when a pipeline run is triggered by an Execute Pipeline activity; Evaluates to Null when used in other circumstances.').returns('String'),
        ];

        const trigParExpressions = [
            triggerParameterExpression('Source', 'pipeline()?.TriggerEvent?.Source', 'Trigger event source. Applicable when the pipeline has an event trigger attached').returns('String'),
            triggerParameterExpression('Subject', 'pipeline()?.TriggerEvent?.Subject', 'Trigger event subject. Applicable when the pipeline has an event trigger attached').returns('String'),
            triggerParameterExpression('Type', 'pipeline()?.TriggerEvent?.Type', 'Trigger event type. Applicable when the pipeline has an event trigger attached').returns('String'),
            triggerParameterExpression('File name', 'pipeline()?.TriggerEvent?.FileName', 'Name of the file whose event triggered the pipeline run. Applicable when the pipeline has a storage event trigger attached').returns('String'),
            triggerParameterExpression('Folder path', 'pipeline()?.TriggerEvent?.FolderPath', 'Path to the folder that contains the file specified by the file name. ' +
                'Applicable when the pipeline has a storage event trigger attached.').returns('String')
        ];

        if (__IS_FABRIC_DPE__ && GlobalVariables.twtParam) {
            trigParExpressions.push(
            triggerParameterExpression('Window start time',
                'pipeline()?.TriggerEvent?.windowStartTimeUtc', 'Window start time of the interval schedule which triggered the pipeline run. Applicable when the pipeline has an interval-based schedule attached').returns('String'),
            triggerParameterExpression('Window end time',
                'pipeline()?.TriggerEvent?.windowEndTimeUtc', 'Window end time of the interval schedule which triggered the pipeline run. Applicable when the pipeline has an interval-based schedule attached').returns('String')
            );
        }

        if (__IS_FABRIC_DPE__) {
            sysVarExpressions.push(systemVariableExpression('Pipeline triggered by pipeline ID', 'pipeline()?.TriggeredByPipeline', 'ID of the pipeline that triggered this pipeline. ' +
                'Applicable when a pipeline run is triggered by an Execute Pipeline activity; Evaluates to Null when used in other circumstances.').returns('String'));
            sysVarExpressions.push(systemVariableExpression('Pipeline Name', 'pipeline().PipelineName', 'Name of the pipeline').returns('String'));
        }

        ExpressionRegistry.register(
            // System variables
            ...sysVarExpressions,
            // Trigger parameters
            ...trigParExpressions,
            // String functions
            functionExpression('concat', 'concat',
                'Combines any number of strings together. For example, if parameter1 is foo, the following expression would return' +
                ' somevalue-foo-somevalue: concat(\'somevalue-\',pipeline().parameters.parameter1,\'-somevalue\')', 'String')
                .args(new Argument('string', 'Required. The strings to combine into a single string.').ofVariableLength())
                .returns('String'),

            functionExpression('substring', 'substring',
                'Returns a subset of characters from a string. For example, the following expression:' +
                ' substring(\'somevalue-foo-somevalue\',10,3) returns: foo', 'String')
                .args(
                    new Argument('originalString', 'Required. The string from which the substring is taken.'),
                    new Argument('startIndex', 'Required. The index of where the substring begins in parameter 1.', 'Int'),
                    new Argument('length', 'Required. The length of the substring.', 'Int'))
                .returns('String'),

            functionExpression('replace', 'replace',
                'Replaces a string with a given string. For example, the expression:' +
                ' replace(\'the old string\', \'old\', \'new\') returns: the new string', 'String')
                .args(
                    new Argument('string', ' Required. If parameter 2 is found in parameter 1, the string that is searched for parameter 2 and updated with parameter 3.'),
                    new Argument('oldString', 'Required. The string to replace with parameter 3 when a match is found in parameter 1'),
                    new Argument('newString', 'Required. The string that is used to replace the string in parameter 2 when a match is found in parameter 1'))
                .returns('String'),

            functionExpression('guid', 'guid',
                'Generates a globally unique string (aka. guid). For example, the following output could be generated' +
                ' c2ecc88d-88c8-4096-912c-d6f2e2b138ce', 'String')
                .args(
                    new Argument('format', ' Optional. A single format specifier that indicates how to format the value of this Guid.' +
                        ' The format parameter can be "N", "D", "B", "P", or "X". If format is not provided, "D" is used.').optional())
                .returns('String'),

            functionExpression('toLower', 'toLower',
                'Converts a string to lowercase. For example, the following returns two by two is four: toLower(\'Two by Two is Four\')', 'String')
                .args(
                    new Argument('string', 'Required. The string to convert to lower casing.' +
                        ' If a character in the string does not have a lowercase equivalent, it is included unchanged in the returned string.'))
                .returns('String'),

            functionExpression('toUpper', 'toUpper',
                'Converts a string to uppercase. For example, the following returns TWO BY TWO IS FOUR: toUpper(\'Two by Two is Four\')', 'String')
                .args(
                    new Argument('string', 'Required. The string to convert to upper casing.' +
                        ' If a character in the string does not have an uppercase equivalent, it is included unchanged in the returned string.'))
                .returns('String'),

            functionExpression('indexof', 'indexof',
                'Find the index of a value within a string case insensitively. For example, the following expression returns 7:' +
                ' indexof(\'hello, world.\', \'world\')', 'String')
                .args(
                    new Argument('string', 'Required. The string that may contain the value.'),
                    new Argument('searchString', 'Required. The value to search the index of.'))
                .returns('Int'),

            functionExpression('lastindexof', 'lastindexof',
                'Find the last index of a value within a string case insensitively. For example, the following expression returns 3:' +
                ' lastindexof(\'foofoo\', \'foo\')', 'String')
                .args(
                    new Argument('string', 'Required. The string that may contain the value.'),
                    new Argument('searchString', 'Required. The value to search the index of.'))
                .returns('Int'),

            functionExpression('startswith', 'startswith',
                'Checks if the string starts with a value case insensitively. For example, the following expression returns true:' +
                ' startswith(\'hello, world\', \'hello\')', 'String')
                .args(
                    new Argument('string', 'Required. The string that may contain the value.'),
                    new Argument('startString', 'Required. The value the string may start with.'))
                .returns('Bool'),

            functionExpression('endswith', 'endswith',
                'Checks if the string ends with a value case insensitively. For example, the following expression returns true:' +
                ' endswith(\'hello, world\', \'world\')', 'String')
                .args(
                    new Argument('string', 'Required. The string that may contain the value.'),
                    new Argument('endString', 'Required. The value the string may end with.'))
                .returns('Bool'),

            functionExpression('split', 'split',
                'Splits the string using a separator. For example, the following expression returns ["a", "b", "c"]: split(\'a;b;c\',\';\')', 'String')
                .args(
                    new Argument('string', 'Required. The string that is split.'),
                    new Argument('separator', 'Required. The separator.'))
                .returns('Array', 'Any'),

            functionExpression('trim', 'trim',
                'Remove leading and trailing whitespace from a string, and return the updated string.', 'String')
                .args(
                    new Argument('string', 'Required. The string that has the leading and trailing whitespace to remove.'))
                .returns('String'),

            // Collection functions
            overloadedFunctionExpression('contains', 'contains',
                'Returns true if dictionary contains a key, list contains value, or string contains ' +
                'substring. For example, the following expression returns true: contains(\'abacaba\',\'aca\')', 'Collection')
                .overload(
                    new Argument('stringWithin', 'Required. The string to search within.', 'String'),
                    new Argument('findString', 'Required. The substring to find inside the Within string.', 'String')).returns('Bool')
                .overload(
                    new Argument('arrayWithin', 'Required. The array to search within.').arrayOf('Any'),
                    new Argument('element', 'Required. The element to find in the array', 'Any')).returns('Bool')
                .overload(
                    new Argument('objectWithin', 'Required. The object to search within.', 'Object'),
                    new Argument('key', 'Required. The key to find in the object', 'String')).returns('Bool')
                .build(),

            overloadedFunctionExpression('length', 'length',
                'Returns the number of elements in an array or string. For example, the following' +
                ' expression returns 3: length(\'abc\')', 'Collection')
                .overload(
                    new Argument('collection', 'Required. The array to get the length of.', 'Array').arrayOf('Any')).returns('Int')
                .overload(
                    new Argument('string', 'Required. The string to get the length of.', 'String')).returns('Int')
                .build(),

            overloadedFunctionExpression('empty', 'empty',
                'Returns true if object, array, or string is empty. For example, the following' +
                ' expression returns true: empty(\'\')', 'Collection')
                .overload(
                    new Argument('collection', 'Required. The array to check if it is empty.', 'Array').arrayOf('Any')).returns('Bool')
                .overload(
                    new Argument('string', 'Required. The string to check if it is empty.', 'String')).returns('Bool')
                .overload(
                    new Argument('object', 'Required. The object to check if it is empty.', 'Object')).returns('Bool')
                .build(),

            overloadedFunctionExpression('intersection', 'intersection',
                'Returns a single array or object with the common elements between the' +
                ' arrays or objects passed to it. For example, this function returns [1, 2]: intersection([1, 2, 3], [101, 2, 1, 10],[6, 8, 1, 2]).' +
                ' The parameters for the function can either be a set of objects or a set of arrays (not a mixture thereof). If there are two objects with the same name,' +
                ' the last object with that name appears in the final object.', 'Collection')
                .overload(
                    new Argument('collections', 'Required. The collections to evaluate. An object must be in all collections passed in to appear in the result.')
                        .arrayOf('Any').ofVariableLength()).returns('Array', 'Any')
                .overload(
                    new Argument('objects', 'Required. The objects to evaluate. An object must be in all objects passed in to appear in the result.', 'Object')
                        .ofVariableLength()).returns('Object')
                .build(),

            overloadedFunctionExpression('union', 'union',
                'Returns a single array or object with all of the elements that are in either array or ' +
                'object passed to it. For example, this function returns [1, 2, 3, 10, 101] : union([1, 2, 3], [101, 2, 1, 10]). The parameters for the function' +
                ' can either be a set of objects or a set of arrays (not a mixture thereof). If there are two objects with the same name in the final output, ' +
                'the last object with that name appears in the final object.', 'Collection')
                .overload(
                    new Argument('collections', 'Required. The collections to evaluate. An object that appears in any of the collections appears in the result.')
                        .arrayOf('Any').ofVariableLength()).returns('Array', 'Any')
                .overload(
                    new Argument('objects', 'Required. The objects to evaluate. An object that appears in any of the collections appears in the result.', 'Object')
                        .ofVariableLength()).returns('Object')
                .build(),

            functionExpression('join', 'join',
                'Return a string that has all the items from an array and has each character separated by a delimiter.', 'Collection')
                .args(
                    new Argument('collection', 'Required. The array that has the items to join.')
                        .arrayOf('Any'),
                    new Argument('delimiter', 'Required. The separator that appears between each character in the resulting string.', 'String'))
                .returns('String'),

            overloadedFunctionExpression('first', 'first',
                'Returns the first element in the array or string passed in. For example, this function returns 0: first([0,2,3])', 'Collection')
                .overload(
                    new Argument('collections', 'Required. The collection to take the first object from.')
                        .arrayOf('Any')).returns('Any')
                .overload(
                    new Argument('string', 'Required. The string to take the first character from.', 'String')).returns('String')
                .build(),

            overloadedFunctionExpression('last', 'last',
                'Returns the last element in the array or string passed in. For example, this function returns 3: last([0,2,3])', 'Collection')
                .overload(
                    new Argument('collections', 'Required. The collection to take the last object from.')
                        .arrayOf('Any')).returns('Any')
                .overload(
                    new Argument('string', 'Required. The string to take the last character from.', 'String')).returns('String')
                .build(),

            overloadedFunctionExpression('take', 'take',
                'Returns the first Count elements from the array or string passed in, for example this function' +
                ' returns [1, 2]: take([1, 2, 3, 4], 2)', 'Collection')
                .overload(
                    new Argument('collection', 'Required. The collection to take the first Count objects from.')
                        .arrayOf('Any'),
                    new Argument('count', 'Required. The number of objects to take from the Collection. Must be a positive integer.', 'Int')
                )
                .returns('Array', 'Any')
                .overload(
                    new Argument('string', 'Required. The string to take the first Count characters from.', 'String'),
                    new Argument('count', 'Required. The number of characters to take from the string. Must be a positive integer.', 'Int')
                )
                .returns('String')
                .build(),

            functionExpression('skip', 'skip',
                'Returns the elements in the array starting at index Count, for example this function returns [3, 4]:' +
                ' skip([1, 2 ,3 ,4], 2)', 'Collection')
                .args(
                    new Argument('collection', 'Required. The collection to skip the first Count objects from.')
                        .arrayOf('Any'),
                    new Argument('count', 'Required. The number of objects to remove from the front of Collection. Must be a positive integer.', 'Int'))
                .returns('Array', 'Any'),

            // Logical functions
            functionExpression('equals', 'equals',
                'Returns true if two values are equal. For example, if parameter1 is foo, the following expression would' +
                ' return true: equals(pipeline().parameters.parameter1, \'foo\')', 'Logical')
                .args(
                    new Argument('object1', 'Required. The object to compare to Object 2.', 'Any'),
                    new Argument('object2', 'Required. The object to compare to Object 1.', 'Any'))
                .returns('Bool'),

            overloadedFunctionExpression('less', 'less',
                'Returns true if the first argument is less than the second. Note, values can only be of type integer, float,' +
                ' or string. For example, the following expression returns true: less(10,100)', 'Logical')
                .overload(
                    new Argument('integer1', 'Required. The integer to check if it is less than integer 2.', 'Int'),
                    new Argument('integer2', 'Required. The integer to check if it is greater than integer 1.', 'Int')).returns('Bool')
                .overload(
                    new Argument('float1', 'Required. The float to check if it is less than float 2.', 'Float'),
                    new Argument('float2', 'Required. The float to check if it is greater than float 1.', 'Float')).returns('Bool')
                .overload(
                    new Argument('string1', 'Required. The string to check if it is less than string 2.', 'String'),
                    new Argument('string2', 'Required. The string to check if it is greater than string 1.', 'String')).returns('Bool')
                .build(),

            overloadedFunctionExpression('lessOrEquals', 'lessOrEquals',
                'Returns true if the first argument is less than or equal to the second. Note, values can only be' +
                ' of type integer, float, or string. For example, the following expression returns true: lessOrEquals(10,10)', 'Logical')
                .overload(
                    new Argument('integer1', 'Required. The integer to check if it is less than or equal to integer 2.', 'Int'),
                    new Argument('integer2', 'Required. The integer to check if it is greater than or equal to integer 1.', 'Int')).returns('Bool')
                .overload(
                    new Argument('float1', 'Required. The float to check if it is less than or equal to float 2.', 'Float'),
                    new Argument('float2', 'Required. The float to check if it is greater than or equal to float 1.', 'Float')).returns('Bool')
                .overload(
                    new Argument('string1', 'Required. The string to check if it is less than or equal to string 2.', 'String'),
                    new Argument('string2', 'Required. The string to check if it is greater than or equal to string 1.', 'String')).returns('Bool')
                .build(),

            overloadedFunctionExpression('greater', 'greater',
                'Returns true if the first argument is greater than the second. Note, values can only be of type integer, float,' +
                ' or string. For example, the following expression returns false: greater(10,10)', 'Logical')
                .overload(
                    new Argument('integer1', 'Required. The integer to check if it is greater than integer 2.', 'Int'),
                    new Argument('integer2', 'Required. The integer to check if it is less than integer 1.', 'Int')).returns('Bool')
                .overload(
                    new Argument('float1', 'Required. The float to check if it is greater than float 2.', 'Float'),
                    new Argument('float2', 'Required. The float to check if it is less than float 1.', 'Float')).returns('Bool')
                .overload(
                    new Argument('string1', 'Required. The string to check if it is greater than string 2.', 'String'),
                    new Argument('string2', 'Required. The string to check if it is less than string 1.', 'String')).returns('Bool')
                .build(),

            overloadedFunctionExpression('greaterOrEquals', 'greaterOrEquals',
                'Returns true if the first argument is greater than or equal to the second. Note, values can' +
                ' only be of type integer, float, or string. For example, the following expression returns false: greaterOrEquals(10,100)', 'Logical')
                .overload(
                    new Argument('integer1', 'Required. The integer to check if it is greater than or equal to integer 2.', 'Int'),
                    new Argument('integer2', 'Required. The integer to check if it is less than or equal to integer 1.', 'Int')).returns('Bool')
                .overload(
                    new Argument('float1', 'Required. The float to check if it is greater than or equal to float 2.', 'Float'),
                    new Argument('float2', 'Required. The float to check if it is less than or equal to float 1.', 'Float')).returns('Bool')
                .overload(
                    new Argument('string1', 'Required. The string to check if it is greater than or equal to string 2.', 'String'),
                    new Argument('string2', 'Required. The string to check if it is less than or equal to string 1.', 'String')).returns('Bool')
                .build(),

            functionExpression('and', 'and',
                'Returns true if both of the parameters are true. Both arguments need to be Booleans. The following returns' +
                ' false: and(greater(1,10),equals(0,0))', 'Logical')
                .args(
                    new Argument('boolean1', 'Required. The first argument that must be true.', 'Bool'),
                    new Argument('boolean2', 'Required. The second argument must be true.', 'Bool'))
                .returns('Bool'),

            functionExpression('or', 'or',
                'Returns true if either of the parameters are true. Both arguments need to be Booleans. The following' +
                ' returns true: or(greater(1,10),equals(0,0))', 'Logical')
                .args(
                    new Argument('boolean1', 'Required. The first argument that must be true.', 'Bool'),
                    new Argument('boolean2', 'Required. The second argument must be true.', 'Bool'))
                .returns('Bool'),

            functionExpression('not', 'not',
                'Returns true if the parameter is false. The following returns true: not(contains(\'200 Success\',\'Fail\'))', 'Logical')
                .args(new Argument('boolean1', 'The boolean to check.', 'Bool'))
                .returns('Bool'),

            functionExpression('if', 'if',
                'Returns a specified value based on if the expression provided results in true or false. For example, ' +
                'the following returns "yes": if(equals(1, 1), \'yes\', \'no\')', 'Logical')
                .args(
                    new Argument('expression', 'Required. A boolean value that determines which value is returned by the expression.', 'Bool'),
                    new Argument('trueResult', 'Required. The value to return if the expression is true.', 'Any'),
                    new Argument('falseResult', 'Required. The value to return if the expression is false.', 'Any'))
                .returns('Any'),

            // Conversion functions
            functionExpression('int', 'int',
                'Convert the parameter to an integer. For example, the following expression returns 100 as a number, rather than a string:' +
                ' int(\'100\')', 'Conversion')
                .args(new Argument('value', 'Required. The value that is converted to an integer.', 'Any'))
                .returns('Int'),

            functionExpression('string', 'string',
                'Convert the parameter to a string. For example, the following expression returns \'10\':' +
                ' string(10). Can also convert an object to a string, for example if the foo parameter is an object with one property bar : baz, then the following would return {"bar" : "baz"}:' +
                ' string(pipeline().parameters.foo)', 'Conversion')
                .args(new Argument('value', 'Required. The value that is converted to a string.', 'Any'))
                .returns('String'),

            functionExpression('json', 'json',
                'Convert the parameter to a JSON type value. It is the opposite of string().' +
                ' For example, the following expression returns [1,2,3] as an array, rather than a string:' +
                ' parse(\'[1,2,3]\'). Likewise, you can convert a string to an object. For example, json(\'{"bar" : "baz"}\') returns:' +
                ' { "bar" : "baz" }', 'Conversion')
                .args(new Argument('string', 'Required. The string that is converted to a native type value.')).returns('Any'),

            functionExpression('float', 'float',
                'Convert the parameter argument to a floating-point number. For example, the following expression returns 10.333:' +
                ' float(\'10.333\')', 'Conversion')
                .args(new Argument('value', 'Required. The value that is converted to a floating-point number.', 'Any'))
                .returns('Float'),

            functionExpression('bool', 'bool',
                'Convert the parameter to a Boolean. For example, the following expression returns false: bool(0)', 'Conversion')
                .args(new Argument('value', 'Required. The value that is converted to a boolean.', 'Any'))
                .returns('Bool'),

            functionExpression('coalesce', 'coalesce',
                'Returns the first non-null object in the arguments passed in. Note: an empty string is not null.' +
                ' For example, if parameters 1 and 2 are not defined, this returns fallback:' +
                ' coalesce(\'pipeline().parameters.parameter1\', pipeline().parameters.parameter2 ,\'fallback\')', 'Conversion')
                .args(new Argument('objects', 'Required. The objects to check for null.', 'Any').ofVariableLength())
                .returns('Any'),

            functionExpression('base64', 'base64',
                'Returns the base64 representation of the input string. For example, the following expression returns c29tZSBzdHJpbmc=:' +
                ' base64(\'some string\')', 'Conversion')
                .args(new Argument('string', 'Required. The string to encode into base64 representation.'))
                .returns('String'),

            functionExpression('base64ToBinary', 'base64ToBinary',
                'Returns a binary representation of a base64 encoded string.' +
                ' For example, the following expression returns the binary representation of some string:' +
                ' base64ToBinary(\'c29tZSBzdHJpbmc=\')', 'Conversion')
                .args(new Argument('string', 'Required. The base64 encoded string.'))
                .returns('Any'),

            functionExpression('base64ToString', 'base64ToString',
                'Returns a string representation of a based64 encoded string.' +
                ' For example, the following expression returns some string:' +
                ' base64ToString(\'c29tZSBzdHJpbmc=\')', 'Conversion')
                .args(new Argument('string', 'Required. The base64 encoded string.'))
                .returns('String'),

            functionExpression('binary', 'binary',
                'Returns a binary representation of a value. For example, the following expression returns a binary representation of some string:' +
                ' binary(\'some string\')', 'Conversion')
                .args(new Argument('value', 'Required. The value that is converted to binary.', 'Any'))
                .returns('Any'),

            functionExpression('dataUriToBinary', 'dataUriToBinary',
                'Returns a binary representation of a data URI.' +
                ' For example, the following expression returns the binary representation of some string:' +
                ' dataUriToBinary(\'data:;base64,c29tZSBzdHJpbmc=\')', 'Conversion')
                .args(new Argument('string', 'Required. The data URI to convert to binary representation.'))
                .returns('Any'),

            functionExpression('dataUriToString', 'dataUriToString',
                'Returns a string representation of a data URI. For example, the following expression returns some string:' +
                ' dataUriToString(\'data:;base64,c29tZSBzdHJpbmc=\')', 'Conversion')
                .args(new Argument('string', 'Required. The data URI to convert to String representation.'))
                .returns('String'),

            functionExpression('dataUri', 'dataUri',
                'Returns a data URI of a value. For example, the following expression returns data:' +
                ' text/plain;charset=utf8;base64,c29tZSBzdHJpbmc=: dataUri(\'some string\')', 'Conversion')
                .args(new Argument('value', 'Required. The value to convert to data URI.'))
                .returns('String'),

            functionExpression('decodeBase64', 'decodeBase64',
                'Returns a string representation of an input based64 string. For example, the following expression returns some string:' +
                ' decodeBase64(\'c29tZSBzdHJpbmc=\')', 'Conversion')
                .args(new Argument('string', 'Returns a string representation of an input based64 string.'))
                .returns('String'),

            functionExpression('encodeUriComponent', 'encodeUriComponent',
                'URL-escapes the string that\'s passed in.' +
                ' For example, the following expression returns You+Are%3ACool%2FAwesome:' +
                ' encodeUriComponent(\'You Are:Cool/Awesome\')', 'Conversion')
                .args(new Argument('string', 'Required. The string to escape URL-unsafe characters from.'))
                .returns('String'),

            functionExpression('decodeUriComponent', 'decodeUriComponent',
                'Un-URL-escapes the string that\'s passed in.' +
                ' For example, the following expression returns You Are:Cool/Awesome:' +
                ' decodeUriComponent(\'You+Are%3ACool%2FAwesome\')', 'Conversion')
                .args(new Argument('string', 'Required. The string to decode the URL-unsafe characters from.'))
                .returns('String'),

            functionExpression('decodeDataUri', 'decodeDataUri',
                'Returns a binary representation of an input data URI string.' +
                ' For example, the following expression returns the binary representation of some string:' +
                ' decodeDataUri(\'data:;base64,c29tZSBzdHJpbmc=\')', 'Conversion')
                .args(new Argument('string', 'Required. The dataURI to decode into a binary representation.'))
                .returns('Any'),

            functionExpression('uriComponent', 'uriComponent',
                'Returns a URI encoded representation of a value. For example, the following expression returns You+Are%3ACool%2FAwesome:' +
                ' uriComponent(\'You Are:Cool/Awesome \')', 'Conversion')
                .args(new Argument('string', 'Required. The string to be URI encoded.'))
                .returns('String'),

            functionExpression('uriComponentToBinary', 'uriComponentToBinary',
                'Returns a binary representation of a URI encoded string.' +
                ' For example, the following expression returns a binary representation of You Are:Cool/Awesome:' +
                ' uriComponentToBinary(\'You+Are%3ACool%2FAwesome\')', 'Conversion')
                .args(new Argument('string', 'Required. The URI encoded string.'))
                .returns('Any'),

            functionExpression('uriComponentToString', 'uriComponentToString',
                'Returns a string representation of a URI encoded string.' +
                ' For example, the following expression returns You Are:Cool/Awesome:' +
                ' uriComponentToString(\'You+Are%3ACool%2FAwesome\')', 'Conversion')
                .args(new Argument('string', 'Required. The URI encoded string.'))
                .returns('String'),

            functionExpression('xml', 'xml',
                'Return an xml representation of the value.' +
                ' For example, the following expression returns an xml content represented by \'\<name>Alan\</name>\':' +
                ' xml(\'\<name>Alan\</name>\'). The xml function supports JSON object input as well. For example, the parameter { "abc": "xyz" } is converted to' +
                ' an xml content \<abc>xyz\</abc>', 'Conversion')
                .args(new Argument('value', 'Required. The value to convert to XML.'))
                .returns('String'),

            functionExpression('xpath', 'xpath',
                'Return an array of xml nodes matching the xpath expression of a value that the xpath expression evaluates to.', 'Conversion')
                .args(
                    new Argument('xml', 'Required. The XML on which to evaluate the XPath expression.'),
                    new Argument('xpath', 'Required. The XPath expression to evaluate.'))
                .returns('Array', 'Any'),

            functionExpression('array', 'array',
                'Convert the parameter to an array. For example, the following expression returns ["abc"]:' +
                ' array(\'abc\')', 'Conversion')
                .args(new Argument('value', 'Required. The value that is converted to an array.', 'Any'))
                .returns('Array', 'Any'),

            functionExpression('createArray', 'createArray',
                'Creates an array from the parameters. For example, the following expression returns ["a", "c"]:' +
                ' createArray(\'a\', \'c\')', 'Conversion')
                .args(new Argument('values', 'Required. The values to combine into an array.', 'Any').ofVariableLength())
                .returns('Array', 'Any'),

            // Math functions
            overloadedFunctionExpression('add', 'add',
                'Returns the result of the addition of the two numbers. For example, this function returns 20.333: add(10,10.333)', 'Math')
                .overload(
                    new Argument('summand1', 'Required. The number to add to Summand 2.', 'Int'),
                    new Argument('summand2', 'Required. The number to add to Summand 1.', 'Int'))
                .returns('Int')
                .overload(
                    new Argument('summand1', 'Required. The number to add to Summand 2.', 'Float'),
                    new Argument('summand2', 'Required. The number to add to Summand 1.', 'Float'))
                .returns('Float')
                .overload(
                    new Argument('summand1', 'Required. The number to add to Summand 2.', 'Int'),
                    new Argument('summand2', 'Required. The number to add to Summand 1.', 'Float'))
                .returns('Float')
                .overload(
                    new Argument('summand1', 'Required. The number to add to Summand 2.', 'Float'),
                    new Argument('summand2', 'Required. The number to add to Summand 1.', 'Int'))
                .returns('Float')
                .build(),

            overloadedFunctionExpression('sub', 'sub',
                'Returns the result of the subtraction of the two numbers. For example, this function returns: -0.333: sub(10,10.333)', 'Math')
                .overload(
                    new Argument('minuend', 'Required. The number that Subtrahend is removed from.', 'Int'),
                    new Argument('subtrahend', 'Required. The number to remove from the Minuend.', 'Int'))
                .returns('Int')
                .overload(
                    new Argument('minuend', 'Required. The number that Subtrahend is removed from.', 'Float'),
                    new Argument('subtrahend', 'Required. The number to remove from the Minuend.', 'Float'))
                .returns('Float')
                .overload(
                    new Argument('minuend', 'Required. The number that Subtrahend is removed from.', 'Int'),
                    new Argument('subtrahend', 'Required. The number to remove from the Minuend.', 'Float'))
                .returns('Float')
                .overload(
                    new Argument('minuend', 'Required. The number that Subtrahend is removed from.', 'Float'),
                    new Argument('subtrahend', 'Required. The number to remove from the Minuend.', 'Int'))
                .returns('Float')
                .build(),

            overloadedFunctionExpression('mul', 'mul',
                'Returns the result of the multiplication of the two numbers. For example, the following returns 103.33: mul(10,10.333)', 'Math')
                .overload(
                    new Argument('multiplicand1', 'Required. The number to multiply Multiplicand 2 with.', 'Int'),
                    new Argument('multiplicand2', 'Required. The number to multiply Multiplicand 1 with.', 'Int'))
                .returns('Int')
                .overload(
                    new Argument('multiplicand1', 'Required. The number to multiply Multiplicand 2 with.', 'Float'),
                    new Argument('multiplicand2', 'Required. The number to multiply Multiplicand 1 with.', 'Float'))
                .returns('Float')
                .overload(
                    new Argument('multiplicand1', 'Required. The number to multiply Multiplicand 2 with.', 'Int'),
                    new Argument('multiplicand2', 'Required. The number to multiply Multiplicand 1 with.', 'Float'))
                .returns('Float')
                .overload(
                    new Argument('multiplicand1', 'Required. The number to multiply Multiplicand 2 with.', 'Float'),
                    new Argument('multiplicand2', 'Required. The number to multiply Multiplicand 1 with.', 'Int'))
                .returns('Float')
                .build(),
            overloadedFunctionExpression('div', 'div',
                'Returns the result of the division of the two numbers. For example, the following returns 1.0333: div(10.333,10)', 'Math')
                .overload(
                    new Argument('dividend', 'Required. The number to divide by the Divisor.', 'Int'),
                    new Argument('divisor', 'Required. The number to divide the Dividend by.', 'Int'))
                .returns('Int')
                .overload(
                    new Argument('dividend', 'Required. The number to divide by the Divisor.', 'Float'),
                    new Argument('divisor', 'Required. The number to divide the Dividend by.', 'Float'))
                .returns('Float')
                .overload(
                    new Argument('dividend', 'Required. The number to divide by the Divisor.', 'Int'),
                    new Argument('divisor', 'Required. The number to divide the Dividend by.', 'Float'))
                .returns('Float')
                .overload(
                    new Argument('dividend', 'Required. The number to divide by the Divisor.', 'Float'),
                    new Argument('divisor', 'Required. The number to divide the Dividend by.', 'Int'))
                .returns('Float')
                .build(),

            functionExpression('mod', 'mod',
                'Returns the result of the remainder after the division of the two numbers (modulo). For example, the following expression returns 2: mod(10,4)', 'Math')
                .args(
                    new Argument('dividend', 'Required. The number to divide by the Divisor.', 'Float'),
                    new Argument('divisor', 'Required. The number to divide the Dividend by. After the division, the remainder is taken.', 'Float'))
                .returns('Int'),

            overloadedFunctionExpression('min', 'min',
                'There are two different patterns for calling this function: min([0,1,2]) Here min takes an array. This expression returns 0.  Alternatively, this ' +
                'function can take a comma-separated list of values: min(0,1,2) This function also returns 0. Note, all values must be numbers, so if the parameter is ' +
                'an array it has to only have numbers in it.', 'Math')
                .overload(new Argument('collection', 'Required. An array of numbers to find the minimum value.').arrayOf('Int')).returns('Int')
                .overload(new Argument('collection', 'Required. An array of numbers to find the minimum value.').arrayOf('Float')).returns('Float')
                .overload(new Argument('values', 'Required. Comma separated numbers.', 'Int').ofVariableLength()).returns('Int')
                .overload(new Argument('values', 'Required. Comma separated numbers.', 'Float').ofVariableLength()).returns('Float')
                .build(),

            overloadedFunctionExpression('max', 'max',
                'There are two different patterns for calling this function: max([0,1,2]) Here max takes an array. This expression returns 2.  Alternatively, this ' +
                'function can take a comma-separated list of values: max(0,1,2) This function also returns 2. Note, all values must be numbers, so if the parameter is ' +
                'an array it has to only have numbers in it.', 'Math')
                .overload(new Argument('collection', 'Required. An array of numbers to find the maximum value.').arrayOf('Int')).returns('Int')
                .overload(new Argument('collection', 'Required. An array of numbers to find the maximum value.').arrayOf('Float')).returns('Float')
                .overload(new Argument('values', 'Required. Comma separated numbers.', 'Int').ofVariableLength()).returns('Int')
                .overload(new Argument('values', 'Required. Comma separated numbers.', 'Float').ofVariableLength()).returns('Float')
                .build(),

            functionExpression('range', 'range',
                'Generates an array of integers starting from a certain number, and you define the length of the returned array.' +
                ' For example, this function returns [3,4,5,6]: range(3,4)', 'Math')
                .args(
                    new Argument('startIndex', 'Required. It is the first integer in the array.', 'Int'),
                    new Argument('count', 'Required. Number of integers that are in the array.', 'Int'))
                .returns('Array', 'Int'),

            functionExpression('rand', 'rand',
                'Generates a random integer within the specified range (inclusive on both ends. For example, this could return 42:' +
                'rand(-1000,1000)', 'Math')
                .args(
                    new Argument('minimum', 'Required. The lowest integer that could be returned.', 'Int'),
                    new Argument('maximum', 'Required. The highest integer that could be returned.', 'Int'))
                .returns('Int'),

            // Date functions
            functionExpression('utcNow', 'utcNow',
                'Returns the current timestamp as a string.', 'Date')
                .args(
                    new Argument('format', 'Either a single format specifier character or a custom format pattern that indicates how to format the value of this timestamp.' +
                        ' If format is not provided, the ISO 8601 format (\'o\') is used.').optional())
                .returns('String'),

            functionExpression('addseconds', 'addseconds',
                'Adds an integer number of seconds to a string timestamp passed in. The number of' +
                ' seconds can be positive or negative. The result is a string in ISO 8601 format ("o") by default, unless a format specifier is provided. ' +
                'For example: addseconds(\'2015-03-15T13:27:36Z\', -36) results in 2015-03-15T13:27:00Z', 'Date')
                .args(
                    new Argument('timestamp', 'Required. A string that contains the time.'),
                    new Argument('seconds', 'Required. The number of seconds to add. May be negative to subtract seconds.', 'Int'),
                    new Argument('format', 'Optional. Either a single format specifier character or a custom format pattern that indicates how to format the value of ' +
                        'this timestamp. If format is not provided, the ISO 8601 format (\'o\') is used.').optional())
                .returns('String'),

            functionExpression('addminutes', 'addminutes',
                'Adds an integer number of minutes to a string timestamp passed in. The number of minutes' +
                ' can be positive or negative. The result is a string in ISO 8601 format ("o") by default, unless a format specifier is provided. For example, 2015-03-15T14:00:36Z:' +
                'addminutes(\'2015-03-15T13:27:36Z\', 33)', 'Date')
                .args(
                    new Argument('timestamp', 'Required. A string that contains the time.'),
                    new Argument('minutes', 'Required. The number of minutes to add. May be negative to subtract minutes.', 'Int'),
                    new Argument('format', 'Optional. Either a single format specifier character or a custom format pattern that indicates how to format the value of ' +
                        'this timestamp. If format is not provided, the ISO 8601 format (\'o\') is used.').optional())
                .returns('String'),

            functionExpression('addhours', 'addhours',
                'Adds an integer number of hours to a string timestamp passed in. The number of hours can be' +
                ' positive or negative. The result is a string in ISO 8601 format ("o") by default, unless a format specifier is provided. For example 2015-03-16T01:27:36Z:' +
                'addhours(\'2015-03-15T13:27:36Z\', 12)', 'Date')
                .args(
                    new Argument('timestamp', 'Required. A string that contains the time.'),
                    new Argument('hours', 'Required. The number of hours to add. May be negative to subtract hours.', 'Int'),
                    new Argument('format', 'Optional. Either a single format specifier character or a custom format pattern that indicates how to format the value of ' +
                        'this timestamp. If format is not provided, the ISO 8601 format (\'o\') is used.').optional())
                .returns('String'),

            functionExpression('adddays', 'adddays',
                'Adds an integer number of days to a string timestamp passed in. The number of days can be positive or negative.' +
                ' The result is a string in ISO 8601 format ("o") by default, unless a format specifier is provided. For example 2015-02-23T13:27:36Z:' +
                'adddays(\'2015-03-15T13:27:36Z\', -20)', 'Date')
                .args(
                    new Argument('timestamp', 'Required. A string that contains the time.'),
                    new Argument('days', 'Required. The number of days to add. May be negative to subtract days.', 'Int'),
                    new Argument('format', 'Optional. Either a single format specifier character or a custom format pattern that indicates how to format the value of ' +
                        'this timestamp. If format is not provided, the ISO 8601 format (\'o\') is used.').optional())
                .returns('String'),

            functionExpression('addToTime', 'addToTime',
                'Add a number of time units to a timestamp. For example, addToTime(\'2018-01-01T00:00:00Z\', 1, \'Day\') results in 2018-01-02T00:00:00.0000000Z', 'Date')
                .args(
                    new Argument('timestamp', 'Required. The string that contains the timestamp.'),
                    new Argument('interval', 'Required. The number of specified time units to add.', 'Int'),
                    new Argument('timeUnit', 'Required. The unit of time to use with interval: "Second", "Minute", "Hour", "Day", "Week", "Month", "Year"', 'String'),
                    new Argument('format', 'Optional. Either a single format specifier character or a custom format pattern that indicates how to format the value of ' +
                        'this timestamp. If format is not provided, the ISO 8601 format (\'o\') is used.').optional())
                .returns('String'),

            functionExpression('convertFromUtc', 'convertFromUtc',
                'Convert a timestamp from Universal Time Coordinated (UTC) to the target time zone. For example, convertFromUtc(\'2018-01-01T08:00:00.0000000Z\', ' +
                '\'Pacific Standard Time\') results in \'2018-01-01T00:00:00.0000000\'', 'Date')
                .args(
                    new Argument('timestamp', 'Required. The string that contains the timestamp.'),
                    new Argument('destinationTimeZone', 'Required. The name for the target time zone.', 'String'),
                    new Argument('format', 'Optional. Either a single format specifier character or a custom format pattern that indicates how to format the value of ' +
                        'this timestamp. If format is not provided, the ISO 8601 format (\'o\') is used.').optional())
                .returns('String'),

            functionExpression('convertTimeZone', 'convertTimeZone',
                'Convert a timestamp from the source time zone to the target time zone. For example, convertTimeZone(\'2018-01-01T80:00:00.0000000Z\', \'UTC\', ' +
                '\'Pacific Standard Time\') results in \'2018-01-01T00:00:00.0000000\'', 'Date')
                .args(
                    new Argument('timestamp', 'Required. The string that contains the timestamp.'),
                    new Argument('sourceTimeZone', 'Required. The name for the source time zone.', 'String'),
                    new Argument('destinationTimeZone', 'Required. The name for the target time zone.', 'String'),
                    new Argument('format', 'Optional. Either a single format specifier character or a custom format pattern that indicates how to format the value of ' +
                        'this timestamp. If format is not provided, the ISO 8601 format (\'o\') is used.').optional())
                .returns('String'),

            functionExpression('convertToUtc', 'convertToUtc',
                'Convert a timestamp from the source time zone to Universal Time Coordinated (UTC). For example, convertToUtc(\'01/01/2018 00:00:00\', \'Pacific Standard Time\') ' +
                'results in \'2018-01-01T08:00:00.0000000Z\'', 'Date')
                .args(
                    new Argument('timestamp', 'Required. The string that contains the timestamp.'),
                    new Argument('sourceTimeZone', 'Required. The name for the source time zone.', 'String'),
                    new Argument('format', 'Optional. Either a single format specifier character or a custom format pattern that indicates how to format the value of ' +
                        'this timestamp. If format is not provided, the ISO 8601 format (\'o\') is used.').optional())
                .returns('String'),

            functionExpression('dayOfMonth', 'dayOfMonth',
                'Return the day of the month from a timestamp.', 'Date')
                .args(
                    new Argument('timestamp', 'Required. The string that contains the timestamp.'))
                .returns('Int'),

            functionExpression('dayOfWeek', 'dayOfWeek',
                'Return the day of the week from a timestamp.', 'Date')
                .args(
                    new Argument('timestamp', 'Required. The string that contains the timestamp.'))
                .returns('Int'),

            functionExpression('dayOfYear', 'dayOfYear',
                'Return the day of the year from a timestamp.', 'Date')
                .args(
                    new Argument('timestamp', 'Required. The string that contains the timestamp.'))
                .returns('Int'),

            functionExpression('formatDateTime', 'formatDateTime',
                'Returns a string in date format. The result is a string in ISO 8601 format ("o") by default, unless a format specifier is provided.', 'Date')
                .args(
                    new Argument('Date', 'Required. A string that contains the date.'),
                    new Argument('Format', ' Optional. Either a single format specifier character or a custom format pattern that indicates how to format the value of this' +
                        ' timestamp. If format is not provided, the ISO 8601 format ("o") is used.').optional())
                .returns('String'),

            functionExpression('getFutureTime', 'getFutureTime',
                'Return the current timestamp plus the specified time units. For example, suppose the current timestamp is \'2018-03-01T00:00:00.0000000Z\', ' +
                'getFutureTime(5, \'Day\') results in \'2018-03-06T00:00:00.0000000Z\'.', 'Date')
                .args(
                    new Argument('interval', 'Required. The number of specified time units to add', 'Int'),
                    new Argument('timeUnit', 'Required. The unit of time to use with interval: "Second", "Minute", "Hour", "Day", "Week", "Month", "Year"', 'String'),
                    new Argument('format', 'Optional. Either a single format specifier character or a custom format pattern that indicates how to format the value of ' +
                        'this timestamp. If format is not provided, the ISO 8601 format (\'o\') is used.').optional())
                .returns('String'),

            functionExpression('getPastTime', 'getPastTime',
                'Return the current timestamp minus the specified time units. For example, suppose the current timestamp is \'2018-02-01T00:00:00.0000000Z\', ' +
                'getPastTime(5, \'Day\') results in \'2018-01-27T00:00:00.0000000Z\'.', 'Date')
                .args(
                    new Argument('interval', 'Required. The number of specified time units to subtract', 'Int'),
                    new Argument('timeUnit', 'Required. The unit of time to use with interval: "Second", "Minute", "Hour", "Day", "Week", "Month", "Year"', 'String'),
                    new Argument('format', 'Optional. Either a single format specifier character or a custom format pattern that indicates how to format the value of ' +
                        'this timestamp. If format is not provided, the ISO 8601 format (\'o\') is used.').optional())
                .returns('String'),

            functionExpression('startOfDay', 'startOfDay',
                'Return the start of the day for a timestamp.', 'Date')
                .args(
                    new Argument('timestamp', 'Required. The string that contains the timestamp.'),
                    new Argument('format', 'Optional. Either a single format specifier character or a custom format pattern that indicates how to format the value of ' +
                        'this timestamp. If format is not provided, the ISO 8601 format (\'o\') is used.').optional())
                .returns('String'),

            functionExpression('startOfHour', 'startOfHour',
                'Return the start of the hour for a timestamp.', 'Date')
                .args(
                    new Argument('timestamp', 'Required. The string that contains the timestamp.'),
                    new Argument('format', 'Optional. Either a single format specifier character or a custom format pattern that indicates how to format the value of ' +
                        'this timestamp. If format is not provided, the ISO 8601 format (\'o\') is used.').optional())
                .returns('String'),

            functionExpression('startOfMonth', 'startOfMonth',
                'Return the start of the month for a timestamp.', 'Date')
                .args(
                    new Argument('timestamp', 'Required. The string that contains the timestamp.'),
                    new Argument('format', 'Optional. Either a single format specifier character or a custom format pattern that indicates how to format the value of ' +
                        'this timestamp. If format is not provided, the ISO 8601 format (\'o\') is used.').optional())
                .returns('String'),

            functionExpression('subtractFromTime', 'subtractFromTime',
                'Subtract a number of time units from a timestamp. For example, subtractFromTime(\'2018-01-02T00:00:00Z\', 1, \'Day\') results in \'2018-01-01T00:00:00:0000000Z\'.', 'Date')
                .args(
                    new Argument('timestamp', 'Required. The string that contains the timestamp.'),
                    new Argument('interval', 'Required. The number of specified time units to subtract.', 'Int'),
                    new Argument('timeUnit', 'Required. The unit of time to use with interval: "Second", "Minute", "Hour", "Day", "Week", "Month", "Year"', 'String'),
                    new Argument('format', 'Optional. Either a single format specifier character or a custom format pattern that indicates how to format the value of ' +
                        'this timestamp. If format is not provided, the ISO 8601 format (\'o\') is used.').optional())
                .returns('String'),

            functionExpression('ticks', 'ticks',
                'Return the ticks property value for a specified timestamp. A tick is a 100-nanosecond interval.', 'Date')
                .args(
                    new Argument('timestamp', 'Required. The string that contains the timestamp.'))
                .returns('Int')
        );
    }
}
